import { resolveVeilSaturationThreatMultiplier, type VeilSaturationId } from './types/veil-saturation';
import type {
    CharacterAttributes,
    CharacterEquipmentLoadout,
    CarriedItem,
    CoreStats,
    Equipment,
    GearComparison,
    Skill,
} from './types/game';
import { getStoredLoadout, resolveWearerDisciplines, getWieldedLoadout } from './equipped-items';
import { isEquippedItem, resolveCoreStats, resolveResourcePools, normalizeSkill } from './skills';
import { isCarriedEquipment } from './crystal-utils';
import { ITEM_LEVEL_MAX } from './item-affixes';
import { resolveWeaponDpsForLevel } from './weapon-tuning';
import { resolveCadenceCooldownMs, resolveCadenceFactor, resolveWeaponBaseAttackCooldownMs } from './attack-timing';
import { clamp } from './utils';
import { FRESH_CHARACTER_ATTRIBUTES } from './constants';
import { createStarterSkills } from './starter-character';
import { findLoadoutNamedItemConflict } from './item-instances';
import { isBothHanded } from './weapon-hands';
import { canEquipInSlot, OFF_HAND_INDEX, type EquipmentGroup } from './equipment-groups';
import { resolveAttackPower, resolveAttackStyle, resolveDamageRange, resolveFocusSkillLevel, resolveOutgoingDamagePercent, resolvePrimaryAttribute, resolveWeaponBaseDamage } from './attack-math';

/** Input to `resolvePlayerPower`: character stats and available items. */
export interface PlayerPowerInput {
    readonly attributes: CharacterAttributes;
    readonly skills: ReadonlyArray<Skill>;
    readonly equipped: CharacterEquipmentLoadout;
    /** Veil Attunement (0-100%), where a character carries it worked out (a run's characters do). */
    readonly veilAttunementPercent?: number;
    /** Essence spent on Essence abilities: Veil Attunement, when no percent is carried. */
    readonly veilAttunementSpent?: number;
    /** An NPC's gear is never weakened by its requirements. */
    readonly type?: string;
    readonly carriedItems?: ReadonlyArray<Equipment | null | undefined>;
    /** Carried items; crystal stacks are ignored (only equipment contributes to power). */
    readonly inventory?: ReadonlyArray<CarriedItem | null | undefined>;
}

/**
 * Character power assessment (#262): the readiness rank and the rating it
 * is rounded down from, the power that rating stands for (what enemy threat
 * and variants scale by), and the equipped loadout's offense/survival split.
 */
export interface PlayerPowerScore {
    readonly power: number;
    /** 1-10: `rating`, rounded down. */
    readonly readiness: number;
    /** 1-10, unrounded: progress in the disciplines, moved by gear at most `GEAR_RATING_LIMIT` either way. */
    readonly rating: number;
    readonly offense: number;
    readonly survival: number;
}

/** Minimum readiness rank (1-10 scale). */
export const PLAYER_READINESS_MIN = 1;
/** Maximum readiness rank (1-10 scale). */
export const PLAYER_READINESS_MAX = 10;
/** Minimum power multiplier clamped in scoring. */
export const PLAYER_POWER_MIN = 0.75;
/** Maximum power multiplier clamped in scoring. */
export const PLAYER_POWER_MAX = 2.5;
/** Minimum threat multiplier for trials. */
export const TRIAL_THREAT_MIN = 1;
/** Maximum threat multiplier for trials. */
export const TRIAL_THREAT_MAX = 3;

/** Crucible NPC threat scaling tuning constants (pending design completion). */
/** Threat for a solo party at reference power (a fresh character). */
const TRIAL_BASE_THREAT = 1;
const TRIAL_POWER_SLOPE = 1.1;
const TRIAL_PARTY_MEMBER_FACTOR = 0.1;

/** Weapon power assessment split between offense and survival (pending design completion). */
const OFFENSE_WEIGHT = 0.6;
const SURVIVAL_WEIGHT = 0.4;

/** Weapon DPS formula tuning constants (pending design completion). */
const HIT_FACTOR_BASE = 0.5;
const HIT_FACTOR_ACCURACY_DIVISOR = 200;
const HIT_FACTOR_MIN = 0.25;
const HIT_FACTOR_MAX = 0.95;

/** Off-hand weapon DPS weight: using an off-hand is more challenging, offset by the multi-hand skill. */
const OFF_HAND_WEIGHT = 0.6;

/**
 * Survival credit for reach past melee, per tile (up to `RANGED_SURVIVAL_TILES_MAX`):
 * a bow's lower damage per second is paid for by not being hit, which the
 * defenses alone don't count (first-pass, #262).
 */
const MELEE_REACH_TILES = 1;
const RANGED_SURVIVAL_PER_TILE = 0.1;
const RANGED_SURVIVAL_TILES_MAX = 8;

/**
 * Readiness tuning (#262, first-pass). Progress is the higher discipline
 * plus this share of the other, so a specialist in either isn't halved.
 */
const SECOND_DISCIPLINE_SHARE = 0.5;
/** Gear moves the rating by at most this, either way. */
const GEAR_RATING_LIMIT = 1;
/** Equipped gear this much stronger than expected (as a power ratio) is worth one rank. */
const GEAR_RATIO_PER_RANK = 1.25;

/**
 * The starter loadout gear is judged against: a freshly created character's
 * creation attributes, the two starter skills at level 1, and the starter
 * shortsword (see `loadStartingEquipment` in the server's
 * `characters/starter-character.ts`). Its offense and survival are where a
 * loadout's score is 1.0.
 */
const REFERENCE_ATTRIBUTES: CharacterAttributes = { ...FRESH_CHARACTER_ATTRIBUTES };

const REFERENCE_SKILLS: Skill[] = createStarterSkills();

const REFERENCE_LOADOUT: CharacterEquipmentLoadout = {
    weapons: [
        {
            id: 'reference-starter-shortsword',
            itemDefinitionId: 'shortsword',
            name: 'Shortsword',
            icon: '',
            equipmentType: 'melee',
            bonuses: { melee: 2 },
            essenceValue: {},
            combat: {
                attackStyle: 'melee',
                // 9 damage per second at 1.3 attacks per second (docs/gameplay/weapon-tuning.md).
                attackCooldownMs: 769,
                attackArcDegrees: 90,
                baseDamage: 6.92,
                damageVariancePercent: 0.2,
                range: 0.75,
                accuracy: 7,
                weaponClass: 'sword',
            },
        },
        null,
    ],
    armor: null,
    miscellaneous: [null, null, null, null],
};

interface LoadoutMetrics {
    readonly offense: number;
    readonly survival: number;
}

type LoadoutSlot = 'primary' | 'offHand' | 'armor' | 'miscellaneous';

const REFERENCE_METRICS = resolveLoadoutScore(REFERENCE_ATTRIBUTES, REFERENCE_SKILLS, REFERENCE_LOADOUT);

/**
 * A character's readiness (#262). Its base is progress in the disciplines,
 * Martial Discipline and Veil Attunement: the higher plus half the other,
 * 0-100% across ranks 1-10, so one lucky drop or a few skill levels can't
 * leap it ahead. Gear then moves it at most one rank: the best loadout the
 * character could wield from what it has equipped and carries (so carrying
 * a strong weapon in unequipped doesn't hide it), by how much stronger or
 * weaker it is than the gear its progress should have, both scored with
 * the character's own attributes and skills. `power` is the power that rating stands for, which enemy threat
 * and variants scale by.
 */
export function resolvePlayerPower(input: PlayerPowerInput): PlayerPowerScore {
    const bestLoadout = selectBestAvailableLoadout(input);
    const metrics = resolveLoadoutScore(input.attributes, input.skills, wieldLoadout(input, bestLoadout));
    const progress = resolveDisciplineProgress(input);
    const gearRatio = safeRatio(scoreMetrics(metrics), scoreLoadout(input, REFERENCE_LOADOUT));
    const expectedGearRatio = resolveExpectedGearRatio(progress);
    const gearShift = clamp(Math.log(gearRatio / expectedGearRatio) / Math.log(GEAR_RATIO_PER_RANK), -GEAR_RATING_LIMIT, GEAR_RATING_LIMIT);
    const rating = clamp(
        PLAYER_READINESS_MIN + (PLAYER_READINESS_MAX - PLAYER_READINESS_MIN) * progress + gearShift,
        PLAYER_READINESS_MIN,
        PLAYER_READINESS_MAX,
    );

    return {
        power: ratingToPower(rating),
        readiness: Math.floor(rating),
        rating,
        offense: metrics.offense,
        survival: metrics.survival,
    };
}

/** Progress in the disciplines, 0..1: the higher plus `SECOND_DISCIPLINE_SHARE` of the other, at most all of it. */
function resolveDisciplineProgress(input: PlayerPowerInput): number {
    const disciplines = resolveWearerDisciplines(input);
    if (!disciplines) {
        return 0;
    }
    const higher = Math.max(disciplines.veilAttunement, disciplines.martialDiscipline);
    const lower = Math.min(disciplines.veilAttunement, disciplines.martialDiscipline);
    return Math.min(100, higher + lower * SECOND_DISCIPLINE_SHARE) / 100;
}

/**
 * How much stronger than the starter loadout a character's gear is expected
 * to score at `progress` (0..1): the weapon level curve
 * (docs/gameplay/weapon-tuning.md) at the drop level that progress has
 * reached, level 1 at none and the top item level at all of it. A score
 * weighs offense by `OFFENSE_WEIGHT`, so the curve's damage ratio is too.
 */
function resolveExpectedGearRatio(progress: number): number {
    const dropLevel = 1 + (ITEM_LEVEL_MAX - 1) * progress;
    return Math.pow(resolveWeaponDpsForLevel(dropLevel) / resolveWeaponDpsForLevel(1), OFFENSE_WEIGHT);
}

/** The power a rating stands for: 1 at rank 1, `PLAYER_POWER_MAX` at rank 10, evenly on a log scale between. */
function ratingToPower(rating: number): number {
    const share = (rating - PLAYER_READINESS_MIN) / (PLAYER_READINESS_MAX - PLAYER_READINESS_MIN);
    return clamp(Math.pow(PLAYER_POWER_MAX, share), PLAYER_POWER_MIN, PLAYER_POWER_MAX);
}

/** How much a loadout's score must rise to count as better gear (or fall, worse): past rounding and the odd point of a stat. */
const BETTER_GEAR_SCORE_RATIO = 1.01;
/** How much it must rise to count as much better, or fall, much worse (first-pass, #341). */
const MUCH_BETTER_GEAR_SCORE_RATIO = 1.15;

/** Wearing an item in one slot: the loadout that makes, and what it takes the place of there. */
interface LoadoutPlacement {
    readonly loadout: CharacterEquipmentLoadout;
    readonly replaced: Equipment | null;
}

/**
 * How wearing `item` compares with the character's equipped loadout (#293,
 * #341), like for like, as the item card compares: in place of the worn
 * item of its own kind in a slot it suits (a sword with the sword in hand,
 * a shield with the shield), else of any worn item in such a slot, and only
 * when it suits no slot that holds anything, in the first empty one. So the
 * sword just swapped out compares with the sword that replaced it, not with
 * a free off hand. Scored the way readiness scores gear (offense and
 * survival with the character's own attributes and skills, as wielded, so
 * gear it falls short of counts for less), against the loadout as it is.
 * The hands and named item rules hold: a weapon that takes both hands
 * empties the off hand, nothing goes beside one, and a loadout the named
 * item rules refuse doesn't count. Undefined when about even, when the item
 * can't be worn anywhere, and for what is already worn.
 */
export function compareWithEquipped(input: PlayerPowerInput, item: Equipment): GearComparison | undefined {
    const equipped = cloneLoadout(getStoredLoadout(input));
    if (!isEquippedItem(item) || flattenLoadout(equipped).some((worn) => worn?.id === item.id)) {
        return undefined;
    }
    const placements: LoadoutPlacement[] = [];
    if (isCompatibleWithSlot(item, 'primary')) {
        const loadout = replaceLoadoutSlot(equipped, 'primary', item);
        if (isBothHanded(item)) {
            loadout.weapons[1] = null;
        }
        placements.push({ loadout, replaced: equipped.weapons[0] });
    }
    if (isCompatibleWithSlot(item, 'offHand') && !isBothHanded(item) && !isBothHanded(equipped.weapons[0])) {
        placements.push({ loadout: replaceLoadoutSlot(equipped, 'offHand', item), replaced: equipped.weapons[1] });
    }
    if (isCompatibleWithSlot(item, 'armor')) {
        placements.push({ loadout: replaceLoadoutSlot(equipped, 'armor', item), replaced: equipped.armor });
    }
    if (isCompatibleWithSlot(item, 'miscellaneous')) {
        equipped.miscellaneous.forEach((worn, index) => placements.push({ loadout: replaceLoadoutSlot(equipped, 'miscellaneous', item, index), replaced: worn }));
    }

    const allowed = placements.filter((placement) => findLoadoutNamedItemConflict(flattenLoadout(placement.loadout)) === undefined);
    const placement = allowed.find((entry) => entry.replaced?.equipmentType === item.equipmentType)
        ?? allowed.find((entry) => entry.replaced)
        ?? allowed[0];
    if (placement === undefined) {
        return undefined;
    }
    const score = scoreLoadout(input, placement.loadout);
    const current = scoreLoadout(input, equipped);
    if (score > current * MUCH_BETTER_GEAR_SCORE_RATIO) {
        return 'much-better';
    }
    if (score > current * BETTER_GEAR_SCORE_RATIO) {
        return 'better';
    }
    if (score * MUCH_BETTER_GEAR_SCORE_RATIO < current) {
        return 'much-worse';
    }
    if (score * BETTER_GEAR_SCORE_RATIO < current) {
        return 'worse';
    }
    return undefined;
}

/** Whether wearing `item` would make the character stronger than its equipped loadout (`compareWithEquipped`). */
export function isBetterThanEquipped(input: PlayerPowerInput, item: Equipment): boolean {
    const comparison = compareWithEquipped(input, item);
    return comparison === 'better' || comparison === 'much-better';
}

/** How each piece of equipment among `items` compares with what the character wears (`compareWithEquipped`), by id; about even and crystal stacks are left out. */
export function resolveGearComparisons(input: PlayerPowerInput, items: ReadonlyArray<CarriedItem | null | undefined>): Record<string, GearComparison> {
    const comparisons: Record<string, GearComparison> = {};
    for (const item of items) {
        const comparison = item && isCarriedEquipment(item) ? compareWithEquipped(input, item) : undefined;
        if (item && comparison !== undefined) {
            comparisons[item.id] = comparison;
        }
    }
    return comparisons;
}

/** Average power level across all party members. */
export function resolvePartyPower(inputs: ReadonlyArray<PlayerPowerInput>): number {
    if (inputs.length === 0) {
        return 1;
    }

    const totalPower = inputs.reduce((total, input) => total + resolvePlayerPower(input).power, 0);
    return totalPower / inputs.length;
}

/** NPC difficulty multiplier for a trial based on party power and size. */
export function resolveTrialThreatMultiplier(inputs: ReadonlyArray<PlayerPowerInput>): number {
    const partyPower = resolvePartyPower(inputs);
    const partySizeFactor = 1 + Math.max(0, inputs.length - 1) * TRIAL_PARTY_MEMBER_FACTOR;
    const partyThreat = partyPower * partySizeFactor;

    return clamp(
        TRIAL_BASE_THREAT + (partyThreat - 1) * TRIAL_POWER_SLOPE,
        TRIAL_THREAT_MIN,
        TRIAL_THREAT_MAX,
    );
}

/** A run's NPC difficulty: its party's Trial threat, raised by the Veil's saturation, never below the floor. */
export function resolveRunThreatMultiplier(inputs: ReadonlyArray<PlayerPowerInput>, veilSaturation: VeilSaturationId): number {
    return Math.max(TRIAL_THREAT_MIN, resolveTrialThreatMultiplier(inputs) * resolveVeilSaturationThreatMultiplier(veilSaturation));
}

function selectBestAvailableLoadout(input: PlayerPowerInput): CharacterEquipmentLoadout {
    const availableItems = collectAvailableItems(input);
    let loadout = cloneLoadout(getStoredLoadout(input));

    loadout = selectBestItemForSlot(input, availableItems, loadout, 'primary');
    loadout = selectBestItemForSlot(input, availableItems, loadout, 'offHand');
    loadout = selectBestItemForSlot(input, availableItems, loadout, 'armor');

    for (let index = 0; index < loadout.miscellaneous.length; index += 1) {
        loadout = selectBestItemForSlot(input, availableItems, loadout, 'miscellaneous', index);
    }

    return loadout;
}

function selectBestItemForSlot(
    input: PlayerPowerInput,
    availableItems: ReadonlyArray<Equipment>,
    currentLoadout: CharacterEquipmentLoadout,
    slot: LoadoutSlot,
    miscellaneousIndex?: number,
): CharacterEquipmentLoadout {
    let bestLoadout = currentLoadout;
    let bestScore = scoreLoadout(input, currentLoadout);

    for (const item of availableItems) {
        if (!isCompatibleWithSlot(item, slot)) {
            continue;
        }

        const candidateLoadout = replaceLoadoutSlot(currentLoadout, slot, item, miscellaneousIndex);
        if (hasDuplicateItem(candidateLoadout)) {
            continue;
        }

        const candidateScore = scoreLoadout(input, candidateLoadout);
        if (candidateScore > bestScore) {
            bestScore = candidateScore;
            bestLoadout = candidateLoadout;
        }
    }

    return bestLoadout;
}

function collectAvailableItems(input: PlayerPowerInput): Equipment[] {
    const allItems = [
        ...flattenLoadout(getStoredLoadout(input)),
        ...(input.carriedItems ?? []),
        ...(input.inventory ?? []).filter(isCarriedEquipment),
    ];
    const seenIds = new Set<string>();
    const availableItems: Equipment[] = [];

    for (const item of allItems) {
        if (!isEquippedItem(item) || seenIds.has(item.id)) {
            continue;
        }

        seenIds.add(item.id);
        availableItems.push(item);
    }

    return availableItems;
}

function cloneLoadout(loadout: CharacterEquipmentLoadout): CharacterEquipmentLoadout {
    return {
        weapons: [loadout.weapons[0], loadout.weapons[1]],
        armor: loadout.armor,
        miscellaneous: [
            loadout.miscellaneous[0],
            loadout.miscellaneous[1],
            loadout.miscellaneous[2],
            loadout.miscellaneous[3],
        ],
    };
}

function replaceLoadoutSlot(
    loadout: CharacterEquipmentLoadout,
    slot: LoadoutSlot,
    item: Equipment,
    miscellaneousIndex?: number,
): CharacterEquipmentLoadout {
    const nextLoadout = cloneLoadout(loadout);

    if (slot === 'primary') {
        nextLoadout.weapons[0] = item;
    } else if (slot === 'offHand') {
        nextLoadout.weapons[1] = item;
    } else if (slot === 'armor') {
        nextLoadout.armor = item;
    } else if (miscellaneousIndex !== undefined) {
        nextLoadout.miscellaneous[miscellaneousIndex] = item;
    }

    return nextLoadout;
}

/** Where each slot sits in the loadout, for the shared equip rule; every accessory slot takes the same items. */
const LOADOUT_SLOT_POSITIONS: Readonly<Record<LoadoutSlot, { readonly group: EquipmentGroup; readonly index: number }>> = {
    primary: { group: 'weapons', index: 0 },
    offHand: { group: 'weapons', index: OFF_HAND_INDEX },
    armor: { group: 'armor', index: 0 },
    miscellaneous: { group: 'miscellaneous', index: 0 },
};

function isCompatibleWithSlot(item: Equipment, slot: LoadoutSlot): boolean {
    const { group, index } = LOADOUT_SLOT_POSITIONS[slot];
    return canEquipInSlot(item.equipmentType, group, index);
}

function hasDuplicateItem(loadout: CharacterEquipmentLoadout): boolean {
    const items = flattenLoadout(loadout).filter(isEquippedItem);
    return new Set(items.map((item) => item.id)).size !== items.length;
}

/**
 * A candidate loadout as this character would wield it: the best loadout is
 * picked from full-strength items, but each is scored with its requirement
 * weakening applied, so gear the character falls short of counts for less.
 */
function wieldLoadout(input: PlayerPowerInput, loadout: CharacterEquipmentLoadout): CharacterEquipmentLoadout {
    return getWieldedLoadout({ ...input, equipped: loadout });
}

function scoreLoadout(input: PlayerPowerInput, loadout: CharacterEquipmentLoadout): number {
    return scoreMetrics(resolveLoadoutScore(input.attributes, input.skills, wieldLoadout(input, loadout)));
}

/** A loadout's offense and survival against the starter loadout's, weighted into one score (1.0 is the starter's). */
function scoreMetrics(metrics: LoadoutMetrics): number {
    const offenseRatio = safeRatio(metrics.offense, REFERENCE_METRICS.offense);
    const survivalRatio = safeRatio(metrics.survival, REFERENCE_METRICS.survival);
    return Math.pow(offenseRatio, OFFENSE_WEIGHT) * Math.pow(survivalRatio, SURVIVAL_WEIGHT);
}

function resolveLoadoutScore(
    attributes: CharacterAttributes,
    skills: ReadonlyArray<Skill>,
    loadout: CharacterEquipmentLoadout,
): LoadoutMetrics {
    const normalizedSkills = skills.map(normalizeSkill);
    const coreStats = resolveCoreStats(attributes, normalizedSkills, loadout);
    const resources = resolveResourcePools(attributes, loadout, undefined, normalizedSkills);
    const primaryWeapon = loadout.weapons[0];
    const offHandWeapon = loadout.weapons[1];
    const primaryDps = resolveWeaponDps(primaryWeapon, coreStats, normalizedSkills, loadout, false);
    const offHandDps = resolveWeaponDps(offHandWeapon, coreStats, normalizedSkills, loadout, true);
    const weaponRange = Math.max(
        primaryWeapon?.combat?.range ?? 0,
        offHandWeapon?.combat?.range ?? 0,
    );
    const offense = Math.max(1, (primaryDps + offHandDps) * (1 + clamp(weaponRange, 0, 10) * 0.02));
    const rangedCredit = 1 + clamp(weaponRange - MELEE_REACH_TILES, 0, RANGED_SURVIVAL_TILES_MAX) * RANGED_SURVIVAL_PER_TILE;
    const defenseSkill = resolveFocusSkillLevel(normalizedSkills, 'defense', loadout);
    const physicalDefense = coreStats.dodge
        + defenseSkill * 2
        + sumCombatValue(loadout, 'defense');
    const essenceDefense = coreStats.resistance
        + defenseSkill * 3
        + sumCombatValue(loadout, 'resistance');
    const survival = Math.max(
        1,
        resources.essence.max
            * Math.sqrt(1 + Math.max(0, physicalDefense) / 100)
            * Math.sqrt(1 + Math.max(0, essenceDefense) / 100)
            * rangedCredit,
    );

    return { offense, survival };
}

function resolveWeaponDps(
    weapon: Equipment | null,
    coreStats: CoreStats,
    skills: ReadonlyArray<Skill>,
    loadout: CharacterEquipmentLoadout,
    isOffHand: boolean,
): number {
    if (!weapon || !isEquippedItem(weapon)) {
        return 0;
    }

    const style = resolveAttackStyle(weapon);
    const primaryStat = resolvePrimaryAttribute(coreStats, style);
    const skillLevel = resolveFocusSkillLevel(skills, style, loadout);
    const { damageMin, damageMax } = resolveDamageRange(resolveWeaponBaseDamage(weapon, isOffHand), weapon.combat?.damageVariancePercent);
    const speedBonus = sumCombatValue(loadout, 'speedBonus');
    const cooldownMs = resolveCadenceCooldownMs(
        resolveWeaponBaseAttackCooldownMs(weapon, isOffHand),
        coreStats.speed + skillLevel + speedBonus,
        isOffHand,
        resolveCadenceFactor(skills, loadout),
    );
    const accuracy = resolveAttackPower(primaryStat, skillLevel, weapon.combat?.accuracy ?? 0);
    const hitFactor = clamp(HIT_FACTOR_BASE + accuracy / HIT_FACTOR_ACCURACY_DIVISOR, HIT_FACTOR_MIN, HIT_FACTOR_MAX);
    const damageFactor = 1 + resolveOutgoingDamagePercent(primaryStat, skillLevel);
    const handWeight = isOffHand ? OFF_HAND_WEIGHT : 1;

    return ((damageMin + damageMax) / 2)
        * damageFactor
        * hitFactor
        * (1000 / cooldownMs)
        * handWeight;
}

function flattenLoadout(loadout: CharacterEquipmentLoadout): Array<Equipment | null> {
    return [
        ...loadout.weapons,
        loadout.armor,
        ...loadout.miscellaneous,
    ];
}

function sumCombatValue(
    loadout: CharacterEquipmentLoadout,
    key: 'defense' | 'resistance' | 'speedBonus',
): number {
    return flattenLoadout(loadout)
        .filter(isEquippedItem)
        .reduce((total, item) => total + (item.combat?.[key] ?? 0), 0);
}


function safeRatio(value: number, baseline: number): number {
    return baseline > 0 ? value / baseline : 1;
}
